import * as React from 'react';
import { useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import { ReloadOutlined } from '@ant-design/icons';
import { flatMap, fromPairs, uniq } from 'lodash';
import { AgentCIWorkflowRequest } from '@/actions/orgSettings';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { platformLabelForApp } from '@/helpers/mdmPlatformOverride';
import { createAppSettingsIntegrationsRoute } from '@/helpers/routeHelpers';
import { useGetAgentCIWorkflowOptionsQuery, useRefreshAgentCIWorkflowsMutation } from '@/middlewares/defaultApi';
import { RootState } from '@/reducers/reducers';
import { selectUserAppDataByAppIdMap, selectUserCanPerformAction } from '@/selectors/selectors';
import { AgentCIAppWorkflows, AgentCIWorkflow } from '@/types';
import { globalMessage } from '@/utils/globalMessage';
import { rwColors } from '../styles/globalStyles';
import { matchSampleWorkflow } from './agentSetupHelpers';
import { InfoTip, monoStyle } from './AgentSetupParts';

export type PickedWorkflows = Record<string, AgentCIWorkflow | undefined>;

export const toCIWorkflowsRequest = (
    workflows: PickedWorkflows,
    appIds: string[],
): Record<string, AgentCIWorkflowRequest> =>
    fromPairs(
        appIds.map((appId) => {
            const { integrationId = '', workflow } = workflows[appId] ?? {};
            return [
                appId,
                {
                    integrationId,
                    workflow: { workflowId: workflow?.workflowId ?? '', appSlug: workflow?.appSlug ?? null },
                },
            ];
        }),
    );

export const useAgentCIWorkflowOptions = (orgId: string, appIds: string[], skip: boolean) => {
    const sortedAppIds = React.useMemo(() => [...appIds].sort(), [appIds]);
    const { data, isFetching } = useGetAgentCIWorkflowOptionsQuery(
        { orgId, appIds: sortedAppIds },
        { skip: skip || sortedAppIds.length === 0 },
    );
    const options = data?.data;
    const isLoading = (appId: string) => isFetching && !options?.[appId];
    return { options, isLoading, isAnyLoading: appIds.some(isLoading) };
};

export const useRefreshAgentWorkflows = (orgId: string, options: Record<string, AgentCIAppWorkflows> | undefined) => {
    const [refreshWorkflows, { isLoading, originalArgs }] = useRefreshAgentCIWorkflowsMutation();
    // The mutation tracks only its latest call, so the table allows one check at a time
    const refreshingAppIds = isLoading ? uniq(originalArgs?.targets.map(({ appId }) => appId)) : [];

    const refresh = (appIds: string[]) => {
        const targets = flatMap(appIds, (appId) =>
            (options?.[appId]?.integrationIds ?? []).map((integrationId) => ({ appId, integrationId })),
        );
        if (targets.length === 0) {
            return;
        }
        refreshWorkflows({ orgId, targets })
            .unwrap()
            .catch((error: { error?: string }) =>
                globalMessage.error(error.error || 'Error refreshing integration data'),
            );
    };

    return { refresh, refreshingAppIds };
};

export const useCanRefreshAgentWorkflows = (appIds: string[]) =>
    useSelector((state: RootState) =>
        fromPairs(
            appIds.map((appId) => [
                appId,
                !!selectUserCanPerformAction(
                    state,
                    RunwayUserAction.UAAddRemoveEditIntegration,
                    undefined,
                    undefined,
                    undefined,
                    false,
                    appId,
                ),
            ]),
        ),
    );

const pickValue = (workflow: AgentCIWorkflow) => `${workflow.integrationId}::${workflow.workflow.workflowId}`;

interface WorkflowTableProps {
    orgId: string;
    appIds: string[];
    options: Record<string, AgentCIAppWorkflows> | undefined;
    isLoading: (appId: string) => boolean;
    // Sample agents run the workflow with this name; without it, a workflow is picked per app
    sampleWorkflowName?: string;
    picked: PickedWorkflows;
    onPick: (appId: string, workflow: AgentCIWorkflow | undefined) => void;
    showErrors: boolean;
    refreshingAppIds: string[];
    onRefresh: (appIds: string[]) => void;
}

export const WorkflowTable: React.FC<WorkflowTableProps> = ({
    orgId,
    appIds,
    options,
    isLoading,
    sampleWorkflowName,
    picked,
    onPick,
    showErrors,
    refreshingAppIds,
    onRefresh,
}) => {
    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, orgId));
    const canRefresh = useCanRefreshAgentWorkflows(appIds);
    const appLabel = (appId: string) => {
        const appData = appDataByAppIdMap[appId];
        return appData ? `${appData.appName} (${platformLabelForApp(appData)})` : appId;
    };
    const noIntegration = (appId: string) => (
        <Text type={showErrors ? 'danger' : 'secondary'}>
            No GitHub Actions integration.{' '}
            <Typography.Link
                href={createAppSettingsIntegrationsRoute(orgId, appId)}
                target="_blank"
                rel="noopener noreferrer"
            >
                Connect it
            </Typography.Link>{' '}
            or remove the app.
        </Text>
    );
    const hasIntegration = (appId: string) => (options?.[appId]?.integrationIds.length ?? 0) > 0;

    const appColumn = {
        title: 'App',
        key: 'app',
        width: 170,
        render: (_: unknown, { appId }: { appId: string }) => appLabel(appId),
    };
    const refreshColumn = {
        title: '',
        key: 'refresh',
        width: 48,
        align: 'right' as const,
        render: (_: unknown, { appId }: { appId: string }) => {
            if (isLoading(appId) || !hasIntegration(appId)) {
                return null;
            }
            const refreshing = refreshingAppIds.includes(appId);
            return (
                <Tooltip
                    title={
                        canRefresh[appId] ? 'Refresh' : (
                            'You need permission to edit integrations to refresh. Runway checks GitHub every 15 minutes.'
                        )
                    }
                >
                    <Button
                        size="small"
                        type="link"
                        aria-label={`Refresh workflows for ${appLabel(appId)}`}
                        icon={<ReloadOutlined spin={refreshing} />}
                        disabled={!canRefresh[appId] || refreshingAppIds.length > 0}
                        onClick={() => onRefresh([appId])}
                    />
                </Tooltip>
            );
        },
    };

    const pickColumns = [
        appColumn,
        {
            title: 'Workflow',
            key: 'workflow',
            render: (_: unknown, { appId }: { appId: string }) => {
                if (isLoading(appId)) {
                    return <Select disabled loading placeholder="Loading workflows…" style={{ width: '100%' }} />;
                }
                if (!hasIntegration(appId)) {
                    return noIntegration(appId);
                }
                const appOptions = options?.[appId];
                const missing = showErrors && !picked[appId];
                return (
                    <div>
                        <Select
                            value={picked[appId] ? pickValue(picked[appId]) : undefined}
                            status={missing ? 'error' : undefined}
                            placeholder="Select workflow"
                            style={{ width: '100%' }}
                            onChange={(value: string) =>
                                onPick(
                                    appId,
                                    appOptions?.workflows.find((workflow) => pickValue(workflow) === value),
                                )
                            }
                            options={[
                                {
                                    label: appOptions?.repo,
                                    options: (appOptions?.workflows ?? []).map((workflow) => ({
                                        value: pickValue(workflow),
                                        label: workflow.workflow.workflowName,
                                    })),
                                },
                            ]}
                        />
                        {missing && <div style={{ color: rwColors.statusRed, marginTop: 4 }}>Select a workflow</div>}
                    </div>
                );
            },
        },
        refreshColumn,
    ];

    const sampleColumns = [
        appColumn,
        {
            title: 'Repository',
            key: 'repo',
            width: 220,
            render: (_: unknown, { appId }: { appId: string }) => {
                if (isLoading(appId)) {
                    return <Text type="secondary">Loading…</Text>;
                }
                if (!hasIntegration(appId)) {
                    return <Text type="secondary">None</Text>;
                }
                return <span style={monoStyle}>{options?.[appId]?.repo}</span>;
            },
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, { appId }: { appId: string }) => {
                if (isLoading(appId)) {
                    return <Text type="secondary">Loading workflows…</Text>;
                }
                if (!hasIntegration(appId)) {
                    return noIntegration(appId);
                }
                if (refreshingAppIds.includes(appId)) {
                    return <Tag color="processing">Checking…</Tag>;
                }
                const appOptions = options?.[appId];
                if (matchSampleWorkflow(appOptions, sampleWorkflowName ?? '')) {
                    return <Tag color="success">Detected</Tag>;
                }
                return <Tag color={showErrors ? 'error' : 'warning'}>Not on the default branch yet</Tag>;
            },
        },
        refreshColumn,
    ];

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text strong>
                Workflow for each app
                <InfoTip
                    title={
                        sampleWorkflowName ?
                            <>{`Runway runs the workflow '${sampleWorkflowName}' in each app's repository.`}</>
                        :   'Pick the workflow Runway runs for each app.'
                    }
                />
            </Text>
            <div style={{ overflowX: 'auto' }}>
                <Table
                    rowKey="appId"
                    size="middle"
                    pagination={false}
                    className="agent-setup-table"
                    columns={sampleWorkflowName ? sampleColumns : pickColumns}
                    dataSource={appIds.map((appId) => ({ appId }))}
                    style={{ minWidth: 600 }}
                />
            </div>
        </Space>
    );
};

export const workflowsForAgent = (
    appIds: string[],
    options: Record<string, AgentCIAppWorkflows> | undefined,
    sampleWorkflowName: string | undefined,
    picked: PickedWorkflows,
): PickedWorkflows | undefined => {
    const workflows = fromPairs(
        appIds.map((appId) => [
            appId,
            sampleWorkflowName ? matchSampleWorkflow(options?.[appId], sampleWorkflowName) : picked[appId],
        ]),
    );
    return appIds.every((appId) => workflows[appId]) ? workflows : undefined;
};
